import { emitSExpr, S } from '@fullstory/util/sexpr';
import { DevToolsMode } from '@fullstory/client-api';
import { CONSOLE_PATH, NETWORK_PATH, DEVTOOLS_PATH } from './playbackmodes';
import { HISTORY_SESSION, HISTORY_SESSION_EXPANDER, HISTORY_SETTINGS } from './apphistory';
import { SessionExpr, userSessionId } from '@fullstory/playback-model';
import { HISTORY_BILLING, HISTORY_LOGOUT } from './settings';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { isSegmentDefinition } from '@fullstory/search-shared';
import { Clip, EventMarker } from '@fullstory/proto/fs/services/alexa';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { SegmentDefinitionWithDisabled } from '@fullstory/search-model';

export const Billing = [HISTORY_SETTINGS, HISTORY_BILLING];
export const Logout = [HISTORY_SETTINGS, HISTORY_LOGOUT];

export function MarkedPlayback(indivId: string, markers: EventMarker[]): string[] {
  return [HISTORY_SESSION, SessionExpr.markersHistoryState(indivId, markers)];
}

export function Playback(indivId: string, userSessionId: string): string[] {
  return [HISTORY_SESSION, `${indivId}:${userSessionId}`];
}

// Provides routing data for session expander.
// If state is present, it should provided as part of replace / push state in order
// to get proper routing (note: this means that new tab / new window navigation should
// not be allowed when set).
export type SessionExpanderRoute = {
  route: string[];
  state?: SessionExpanderState;
};

export type SessionExpanderState = {
  segmentDefinition: SegmentDefinition;
  continuationState?: any;
};

export function isSessionExpanderState(data: any): data is SessionExpanderState {
  return isSegmentDefinition(data?.segmentDefinition);
}

export function SessionExpander(
  indivId: string,
  userSessionId: string,
  segmentDefinition: SegmentDefinition,
  unnamedSegmentId?: string,
): SessionExpanderRoute {
  const sessionExpr: S = { value: '', kids: [{ value: indivId }, { value: userSessionId }] };
  const sexprs = [sessionExpr];
  let state: SessionExpanderState | undefined = undefined;
  if (unnamedSegmentId) {
    // Unnamed segment id can be encoded solely as an sexpr value.
    sexprs.push({ value: unnamedSegmentId });
  } else {
    state = {
      segmentDefinition,
    };
  }
  return {
    route: [HISTORY_SESSION_EXPANDER, emitSExpr(sexprs)],
    state,
  };
}

export const appendToSessionPath = (devtoolsMode: DevToolsMode) => {
  switch (devtoolsMode) {
    case DevToolsMode.Console:
      return [DEVTOOLS_PATH, CONSOLE_PATH];
    case DevToolsMode.Network:
      return [DEVTOOLS_PATH, NETWORK_PATH];
    default:
    case DevToolsMode.None:
      return [];
  }
};

export function Session(
  indivId: string,
  userSessionId: string,
  startTime: number = 0,
  devtoolsMode: DevToolsMode = DevToolsMode.None,
): string[] {
  const path = [HISTORY_SESSION, SessionExpr.historyState(indivId, userSessionId, startTime)];
  return path.concat(appendToSessionPath(devtoolsMode));
}

interface hasCurrentSegmentDefinition {
  currentSegmentDefinition: () => SegmentDefinitionWithDisabled;
}
export function SessionOrMarkedPlayback(
  ctx: hasCurrentSegmentDefinition,
  indvId: string,
  userIdSessionId: string,
  needsMarkers: boolean,
  segmentDefinition?: SegmentDefinition,
): SessionExpanderRoute {
  // TODO(nishanth): Make the segmentDefinition parameter non-optional, thus forcing
  // callers to specify the segment dewfinition that should be used, instead of
  // implicity using ctx.currentSegmentDefinition() here.
  const currentSegmentDefinition = segmentDefinition || ctx.currentSegmentDefinition().definition;
  if (needsMarkers) {
    return SessionExpander(indvId, userIdSessionId, currentSegmentDefinition);
  } else {
    return {
      route: Session(indvId, userIdSessionId),
    };
  }
}

export interface PlaybackLink {
  indivId: string;
  userSessionId: string;
  lastWhen: Date | null;
  when: Date | null;
  markers?: EventMarker[] | null;
  numEvents?: number;
  duration?: number;
  second?: boolean;
}

// Got clips. Coalesce them intelligently.
export function linksFromClips(indivId: string, clips: Clip[]): PlaybackLink[] {
  const links: PlaybackLink[] = [];
  let indiv = indivId;

  let prevLink: PlaybackLink | undefined = undefined;
  for (let i = 0; i < clips.length; ++i) {
    const clip = clips[i];
    const firstMarker = clip?.eventMarkers?.[0];

    if (!indiv && firstMarker?.indvId) {
      indiv = firstMarker.indvId as string;
    }

    // If there's only one marker, and it's in the same user/session as the previous clip's marker,
    // just roll them together into a single link.
    if (clip?.eventMarkers?.length == 1) {
      if (
        prevLink &&
        firstMarker &&
        prevLink.userSessionId == userSessionId(firstMarker.userId, firstMarker.sessionId)
      ) {
        prevLink.markers?.push(firstMarker);
        if (prevLink.numEvents) ++prevLink.numEvents;
        continue;
      }
    }

    // Make a new link.
    const lastMarker = i < clips.length - 1 ? clips[i + 1]?.eventMarkers?.[0] : null;
    prevLink = {
      indivId: indiv,
      userSessionId: clip?.eventMarkers?.[0]?.userId
        ? userSessionId(clip.eventMarkers[0].userId ?? '', clip.eventMarkers[0].sessionId ?? '')
        : '',
      lastWhen:
        lastMarker && lastMarker.pageStartAt
          ? new Date(Timestamp.toMillis(lastMarker.pageStartAt) + lastMarker.pageOffset)
          : null,
      when:
        firstMarker && firstMarker.pageStartAt
          ? new Date(Timestamp.toMillis(firstMarker.pageStartAt) + firstMarker.pageOffset)
          : null,
      markers: clip?.eventMarkers,
      numEvents: 1,
    };
    links.unshift(prevLink); // Unshift to get them in reverse-time order.
  }

  return links;
}

export function historyIdFromLink(link: PlaybackLink): string {
  return historyIdFromMarkers(link.indivId, link.userSessionId, link.markers ?? []);
}

export function historyIdFromMarkers(
  indvId: string,
  userSessionId: string,
  markers: EventMarker[],
): string {
  if (markers.length) {
    const expr = SessionExpr.fromMarkers(indvId, markers);
    return expr.isSingleSession()
      ? emitSExpr(expr.sexprSessionOnly())
      : SessionExpr.markersHistoryState(indvId, markers);
  } else {
    return SessionExpr.historyState(indvId, userSessionId, 0);
  }
}
